import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

interface FavoritesContextType {
  favorites: Set<string>;
  toggleFavorite: (productId: string, productData?: any) => void;
  isFavorite: (productId: string) => boolean;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  // Load favorites from localStorage on mount
  useEffect(() => {
    const storedFavorites = localStorage.getItem('favorites');
    if (storedFavorites) {
      try {
        const parsedFavorites = JSON.parse(storedFavorites);
        setFavorites(new Set(parsedFavorites));
      } catch (error) {
        console.error('Error parsing favorites from localStorage:', error);
      }
    }
  }, []);

  // Save favorites to localStorage whenever favorites change
  useEffect(() => {
    localStorage.setItem('favorites', JSON.stringify(Array.from(favorites)));
  }, [favorites]);

  const toggleFavorite = (productId: string, productData?: any) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      const isRemoving = newFavorites.has(productId);
      
      if (isRemoving) {
        newFavorites.delete(productId);
        toast.success('הוסר מהמועדפים');
        
        // Track remove from wishlist
        if (typeof window !== 'undefined' && window.gtag && productData) {
          window.gtag('event', 'remove_from_wishlist', {
            currency: 'ILS',
            value: productData.price_per_unit || productData.price || 0,
            items: [{
              item_id: productId,
              item_name: productData.name || 'Unknown Product',
              price: productData.price_per_unit || productData.price || 0,
              quantity: 1
            }]
          });
        }
      } else {
        newFavorites.add(productId);
        toast.success('נוסף למועדפים');
        
        // Track add to wishlist
        if (typeof window !== 'undefined' && window.gtag && productData) {
          window.gtag('event', 'add_to_wishlist', {
            currency: 'ILS',
            value: productData.price_per_unit || productData.price || 0,
            items: [{
              item_id: productId,
              item_name: productData.name || 'Unknown Product',
              item_category: productData.categories?.name || productData.category_name,
              price: productData.price_per_unit || productData.price || 0,
              quantity: 1
            }]
          });
        }
      }
      return newFavorites;
    });
  };

  const isFavorite = (productId: string) => {
    return favorites.has(productId);
  };

  return (
    <FavoritesContext.Provider value={{ favorites, toggleFavorite, isFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
};